import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  Row,
  SortingFn,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import moment from 'moment'

import {removeDragTagPreview, setDragTagPreview} from '@/shared/utils/dragTagPreview'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {
  createContext,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {useNavigate} from 'react-router-dom'
import {useLayoutPanelsOptional} from '@/shared/context/ChatContext'
import {useContextMenu} from 'react-contexify'
import 'react-contexify/dist/ReactContexify.css'
import {toast} from 'react-toastify'
import {
  convertTimestampToDateString,
  copylinksToClipboard,
  CustomFileData,
  dbItem,
  DragFileData,
  findIds,
  formatBytes,
  getLogger,
  isDebugLogEnabled,
  RenameProps,
  useFetchFiles,
  useFileMap,
  FILES_LIST_GC_TIME_MS,
  clearUserSessionLocalStorage,
} from '@/shared/utils'
import {ImportSourceType} from '../core/_requests'
import {FileIcon} from '@/ui/components/FileIcon'
import {
  toastActionableConfig,
  toastDefaultConfig,
  toastInfiniteConfig,
} from '@/shared/config/toastConfig'
import {useCognitoAuth} from '../../auth'
import {useRegisterFileImportActions} from './FileImportActionsContext'
import {
  assertUploadSize,
  collectDroppedFiles,
  DroppedContent,
  DroppedEntries,
  runLimited,
  TooManyFilesError,
  fileQueryKeys,
  fileStoreFor,
  folderListQuery,
  OWN_ROOT,
  shareObjectKey,
  ShareLocation,
  StorageRoot,
  uploadFolder,
} from '../core/storage'
import {FolderMove, intoFolderMoves, pinsQueryKeys, renameMoves, usePinActions} from '../../pins'
import {
  dropShareCaches,
  isShareUnavailable,
  shareRootFromView,
  sharingQueryKeys,
  ShareView,
  useShares,
  useSharingAccess,
} from '../../sharing'
import {shareErrorCode} from '../../sharing/errors'
import {
  isSharedFolderLocked,
  movesSharedFolder,
  sharedFolderLockedMessage,
} from '../../sharing/ownerLocks'
import {sharedWithLabel} from '../../sharing/format'
import {shareGoneMessage, subscribeShareGone} from '../../sharing/shareEvents'
import {
  ShareMoveIntent,
  ShareMoveOutcome,
  ShareMoveProvider,
  useOpenShareMove,
} from '../../sharing/ShareMoveDialog'
import {shareOperationErrorMessage} from '../../sharing/shareOperations'
import {ShareTransferOutcome, ShareTransferProvider} from '../../sharing/ShareTransferDialog'
import {ShareUploadItem, uploadBatchToShare} from '../../sharing/shareUploadBatch'
import {CustomModal} from './CustomModal'
import {ItemNameModal} from './ItemNameModal'
import {DropZone} from './DropZone'
import {collapseProps} from './external-viewer/collapsible-card'
import {openFileInViewer} from './external-viewer/open-in-viewer'
import {resolveOpenability} from './external-viewer/plain-file-formats'
import {matchesFilters} from './file-picker/filePickMessages'
import {FileTableHeader} from './FileTableHeader'
import {describeFolderStatus} from './fileTableStatus'
import {FileTableMenu, MENU_ID} from './FileTableMenu'
import {FileTableSkeleton} from './FileTableSkeleton'
import {FilesClipboardProvider} from './FilesClipboardContext'
import {invalidateChangedFolders} from './fileListInvalidation'
import {BrowserRoot, SHARED_INDEX, storageRootOf, viewerTarget} from './fileBrowserRoot'
import {
  LocationView,
  readStoredLocation,
  storeLocation,
  ToolbarActions,
  toolbarActions,
  TOP_FOLDER,
} from './fileBrowserLocation'
import {pinnedFolderIds, pinnedShareIds, sharePinCandidates} from './pinnedFolders'
import {PinnedName} from './PinnedName'
import {
  SHARED_ROW_MENU_ID,
  SharedFolderList,
  SharedRootRow,
  SharedRowMenu,
  SharedRowMenuTarget,
} from './SharedFolderList'
import {
  FILE_TABLE_CELL_CONTENT_CLASS,
  fileTableCellContentStyle,
  fileTableCellStyle,
  fileTableHeaderCellStyle,
  fileTableHeaderRowStyle,
  fileTableStyle,
} from './fileTableStyles'
import './shared-folders.scss'
import {ImportWizard} from './ImportWizard'
import useFileTable from './useFileTable'
import {useSocket} from './SocketProvider'
import {useOntologyTree} from '@/shared/utils/hooks/useOntologyTree'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import type {QueryClient} from '@tanstack/react-query'
declare module '@tanstack/table-core' {
  interface SortingFns {
    foldersFirstSort?: SortingFn<unknown>
  }
}

const ROOT_FOLDER = ''

const NO_PINNED_IDS: ReadonlySet<string> = new Set()

/** The status line on the Shared root. */
const sharedIndexStatus = (count: number) =>
  count === 1 ? '1 shared folder' : `${count} shared folders`

//React table configuration
/** Extra data the FileTable passes to its (module-level) column cells via table meta. */
interface FileTableMeta {
  /** From the ontology tree: true/false openable, or undefined when unknown. */
  isFileOpenable?: (file: CustomFileData) => boolean | undefined
  /** Panel layout: Shared column is hidden; the link icon sits after the file name. */
  showShareIconOnName?: boolean
  /** In My Files: the share of a folder the user shares with others (D9), if it is one. */
  sharedFolderFor?: (file: CustomFileData) => ShareView | undefined
}

/**
 * Lazily-created portal host for the file tooltips.
 *
 * react-bootstrap portals overlays into `document.body` by default. During Popper's first
 * positioning pass the freshly-appended tooltip transiently overflows the document, which makes
 * the body grow a vertical scrollbar for ~20ms; the resulting 15px width loss reflows the whole
 * `d-flex flex-lg-row` layout, visibly resizing the Results panel and the viewer iframe inside it
 * before snapping back. (Measured with a ResizeObserver — before/after sampling misses it, which
 * is why switching Popper's `strategy` appeared to change nothing.)
 *
 * `position: fixed` + `overflow: hidden` makes this host incapable of contributing to the
 * document's scroll size, so nothing the tooltip does mid-pass can affect page layout.
 * `pointer-events: none` keeps it from swallowing clicks (the tooltip itself re-enables them).
 */
const TOOLTIP_HOST_ID = 'ob-file-tooltip-host'

function getTooltipHost(): HTMLElement | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }
  let host = document.getElementById(TOOLTIP_HOST_ID)
  if (!host) {
    host = document.createElement('div')
    host.id = TOOLTIP_HOST_ID
    // Above `.modal-dialog` (custom.scss:1409 sets it to 100000). The browser also renders inside
    // the wizard file-pick modal now, and at the old 1080 the tooltips were painted behind it.
    host.style.cssText =
      'position:fixed; top:0; left:0; width:100%; height:100%; overflow:hidden; pointer-events:none; z-index:100001;'
    document.body.appendChild(host)
  }
  return host
}

/**
 * Name cell for a file row: icon + (possibly truncated) name, with a hover
 * tooltip. When the file's tags resolve to a known ontology term, the
 * tooltip shows the term's label and (if the exporter published one) its
 * description, instead of just the raw filename — a plain `title` attribute
 * can't render that multi-line/styled content. Falls back to the native
 * `title` (name, plus the "no web viewer" clue) when no ontology info is
 * available, e.g. folders or files the tree doesn't recognize. In debug/
 * verbose mode ({@link isDebugLogEnabled}, `REACT_APP_DEBUG_LOG=1`) the
 * tooltip also appends the raw ontology term URI after the label.
 */
export const FileNameCell: React.FC<{
  file: CustomFileData
  name: string
  noViewer: boolean
  showShareIcon?: boolean
  /**
   * Set on a folder the user shares (D9): a "Shared with N" label, so the row's tint is never the
   * only cue.
   */
  sharedFolder?: {memberCount: number}
}> = ({file, name, noViewer, showShareIcon = false, sharedFolder}) => {
  const {resolver} = useOntologyTree()
  const ontologyInfo = resolver.ontologyInfo(file.tags)

  const nameDiv = (
    <div
      className={noViewer ? 'ob-file-name-text ob-file-no-viewer' : 'ob-file-name-text'}
      style={{
        paddingLeft: '5px',
      }}
      title={ontologyInfo ? undefined : noViewer ? `${name} — no web viewer available` : name}
    >
      {name}
    </div>
  )

  return (
    // Flex, shrinkable below its content (see .ob-file-name-cell in shared-folders.scss).
    <div className='ob-file-name-cell'>
      <FileIcon customFile={file} />
      <PinnedName pinned={!!file.pinned}>
        {ontologyInfo ? (
          <OverlayTrigger
            placement='top'
            // Both of these keep the tooltip from perturbing page layout — see getTooltipHost.
            container={getTooltipHost()}
            popperConfig={{strategy: 'fixed'}}
            // OverlayTrigger only enables Popper's flip modifier for 'auto*' placements, so a fixed
            // 'top' placement would render above the viewport for the topmost rows — and the host's
            // `overflow: hidden` would then clip it away entirely. Flip to the bottom instead.
            flip
            overlay={
              <Tooltip id={`ontology-tooltip-${file.id}`}>
                <div style={{textAlign: 'left'}}>
                  <div className='fw-bold'>
                    {ontologyInfo.label}
                    {/* Debug/verbose mode (REACT_APP_DEBUG_LOG=1): surface the raw ontology term
                      URI so it can be cross-checked against ontology-tree.json / tags.ontology. */}
                    {isDebugLogEnabled() && ` (${ontologyInfo.uri})`}
                  </div>
                  {ontologyInfo.description && <div>{ontologyInfo.description}</div>}
                  {noViewer && <div className='fst-italic'>No web viewer available</div>}
                </div>
              </Tooltip>
            }
          >
            {nameDiv}
          </OverlayTrigger>
        ) : (
          nameDiv
        )}
      </PinnedName>
      {showShareIcon && file.shared ? (
        <i
          className='fas fa-link ob-file-share-icon'
          title='Copy link'
          onClick={(e) => {
            e.stopPropagation()
            copylinksToClipboard([file.id])
          }}
        />
      ) : null}
      {sharedFolder ? (
        <span className='ob-shared-folder-badge' data-testid='shared-folder-badge'>
          {sharedWithLabel(sharedFolder.memberCount)}
        </span>
      ) : null}
    </div>
  )
}

const columnHelper = createColumnHelper<CustomFileData>()
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    cell: ({row, getValue, table}) => {
      // Small clue when the ontology tree says the file has no web viewer.
      // `undefined` (folder / unknown / tree not loaded) shows nothing. Style
      // lives in the `.ob-file-no-viewer` CSS class (see custom.scss).
      const openable = (table.options.meta as FileTableMeta | undefined)?.isFileOpenable?.(
        row.original
      )
      const noViewer = !row.original.isDir && openable === false
      const meta = table.options.meta as FileTableMeta | undefined
      const showShareIcon = meta?.showShareIconOnName === true
      return (
        <FileNameCell
          file={row.original}
          name={getValue()}
          noViewer={noViewer}
          showShareIcon={showShareIcon}
          sharedFolder={meta?.sharedFolderFor?.(row.original)}
        />
      )
    },
    sortingFn: 'foldersFirstSort',
  }),
  columnHelper.accessor('modDate', {
    cell: (info) =>
      info.getValue() ? (
        <div
          className='ob-file-date-text'
          title={moment(info.getValue()).format('DD/MM/yyyy HH:mm:ss')} // Show full date on hover
        >
          {moment(info.getValue()).format('DD/MM/yyyy HH:mm')}
        </div>
      ) : null,
    header: 'Last modified',
    sortingFn: 'foldersFirstSort',
  }),
  columnHelper.accessor('size', {
    cell: (info) =>
      info.getValue() ? (
        <div
          style={{
            cursor: 'default',
            userSelect: 'none',
            whiteSpace: 'nowrap',
            textAlign: 'right',
          }}
        >
          {formatBytes(info.getValue()!, 0)}
        </div>
      ) : null,
    header: 'Size',
    sortingFn: 'foldersFirstSort',
  }),
  columnHelper.accessor('shared', {
    cell: (info) =>
      info.getValue() ? (
        <div className='d-flex justify-content-center'>
          <i
            className='fas fa-link'
            style={{cursor: 'pointer', textAlign: 'center', userSelect: 'none'}}
            title='Copy link'
            onClick={(e) => {
              e.stopPropagation()
              copylinksToClipboard([info.row.original.id])
            }}
          ></i>
        </div>
      ) : null,
    header: () => <div className='d-flex justify-content-center'>Shared</div>,
  }),
]

export interface FileTableContextValue {
  showRenameModal: boolean
  setShowRenameModal: (show: boolean) => void
  showDeleteConfirmation: boolean
  setShowDeleteConfirmation: (show: boolean) => void
  resetSelection: (row?: Row<CustomFileData>) => void
  selectedFiles: () => CustomFileData[]
  onReturn: () => void
  onNewFolder: () => void
  /** Go to the folder at this index of {@link folderPath} (0 is the root's top). */
  onBreadCrumb: (index: number) => void
  /**
   * Open a folder of My Files by its path from the root (`''` for the root, `a/b` below it), e.g.
   * where a transfer went. Absent where My Files can't be shown (no subscription, picker).
   */
  onOpenFolder?: (relativePath: string) => void
  onUploadFile: () => void
  onUploadFolder: () => void
  onImportS3: (_sourceType?: ImportSourceType) => void
  onRefresh: () => void
  folderPath: string[]
  folderPathRef: React.MutableRefObject<string[]>
  fileToRename: CustomFileData | undefined
  setFileToRename: (file: CustomFileData) => void
  onRename: () => void
  isRefetching: boolean
  /** Nothing here can be changed: the Shared virtual folder, or a folder shared with Can view. */
  readOnly?: boolean
  /** Whether the user has My Files (false without a subscription). Defaults to true. */
  ownFiles?: boolean
  /**
   * Where the browser is, for the breadcrumb: Home › a, Home › Shared, Home › Shared › share › a.
   * Absent: My Files.
   */
  location?: LocationView
  /** The Home crumb, from the Shared virtual folder or a shared folder: the top of My Files. */
  onGoHome?: () => void
  /** The Shared crumb: the list of shared folders. */
  onGoShared?: () => void
  /** What the toolbar offers where the browser is (see `toolbarActions`). Absent: My Files. */
  toolbar?: ToolbarActions
  /** The root whose files are on screen; absent in the Shared virtual folder. Defaults to My Files. */
  storageRoot?: StorageRoot
}

export const FileTableContext = createContext<FileTableContextValue>({
  showRenameModal: false,
  setShowRenameModal: (show: boolean) => {},
  showDeleteConfirmation: false,
  setShowDeleteConfirmation: (show: boolean) => {},
  resetSelection: (row?: Row<CustomFileData>) => {},
  selectedFiles: () => [] as CustomFileData[],
  onReturn: () => {},
  onNewFolder: () => {},
  onBreadCrumb: (index: number) => {},
  onUploadFile: () => {},
  onUploadFolder: () => {},
  onImportS3: (_sourceType?: ImportSourceType) => {},
  onRefresh: () => {},
  folderPath: [] as string[],
  folderPathRef: [] as unknown as React.MutableRefObject<string[]>,
  fileToRename: {} as CustomFileData | undefined,
  setFileToRename: (file: CustomFileData) => {},
  onRename: () => {},
  isRefetching: false,
})

/**
 * A row handed back to a picker host. The `key` is `CustomFileData.id`, i.e. `dbItem.file`: relative
 * to My Files, or to {@link share} for a file in a folder someone shares with the user.
 */
export interface PickedFile {
  key: string
  name: string
  isDir: boolean
  size: number
  /** The shared folder the file is in, when it is not the user's own. */
  share?: ShareLocation
}

/**
 * Turns the file manager into a read-only browser for a dialog.
 *
 * The presence of this object *is* pick mode. Everything that mutates storage — the context menu,
 * upload, drag and drop, the clipboard shortcuts, the rename/delete/new-folder modals — is switched
 * off, and a double-click chooses a file instead of routing the app to a viewer.
 *
 * Single selection unless {@link multiple} says otherwise.
 */
export interface FileTablePickOptions {
  /** Extension hints, e.g. `['*.psq']`. Folders always pass. Empty/undefined renders no filter row. */
  filters?: string[]
  /** Identity-relative key to preselect once the initial folder's listing resolves. */
  initialSelectedKey?: string
  /**
   * Selection changed. `undefined` when nothing choosable is selected.
   *
   * Still called in {@link multiple} mode, with the first choosable row, so a caller that only reads
   * this keeps working. Unchanged in shape on purpose: every existing picker depends on it.
   */
  onSelect: (file: PickedFile | undefined) => void
  /**
   * Double-click on a file: choose and close.
   *
   * Not called in {@link multiple} mode — a double-click there toggles the row into the selection
   * instead, because closing the dialog on the second click would cut short a selection the user is
   * still building.
   */
  onConfirm: (file: PickedFile) => void
  /** Let a folder be the answer. Default false — files only. */
  selectFolders?: boolean
  /**
   * Allow several rows to be chosen at once, with ctrl/shift-click as the Files panel already uses.
   *
   * Deliberately not a second selection mechanism: both modes go through one internal reporter, so a
   * reset cannot clear one channel and leave the other stale.
   */
  multiple?: boolean
  /** Selection changed, in {@link multiple} mode: every choosable row now chosen, in row order. */
  onSelectMany?: (files: PickedFile[]) => void
}

interface FileTableProps extends collapseProps {
  title: string
  hideTitle?: boolean
  initialPath?: string
  /** `root` says whose space `pathSegments` is in: My Files, the Shared index or a shared folder. */
  onFolderPathChange?: (pathSegments: string[], root: BrowserRoot) => void
  /**
   * 'panel' lifts the chrome actions into the Files lateral-panel header (icon buttons next to
   * close). Layout of the remaining breadcrumbs is otherwise identical.
   */
  variant?: 'default' | 'panel'
  /** Closes the Files lateral panel. Only used when {@link variant} is `'panel'`. */
  onClose?: () => void
  /**
   * False while the lateral panel is collapsed. The component stays mounted (so selection and
   * scroll survive), but its window-level shortcuts must not fire from other pages.
   */
  enabled?: boolean
  /**
   * Bumped by the panel each time a deep link asks for a folder. Without it, re-requesting the
   * folder the user has since navigated away from would be a no-op, because `initialPath` alone
   * would be unchanged.
   */
  pathRequestNonce?: number
  /** See {@link FileTablePickOptions}. Its presence switches the browser into pick mode. */
  pick?: FileTablePickOptions
  /**
   * Remember the folder and sorting in localStorage. Defaults to true, except in pick mode: the
   * keys are shared app-wide, so a dialog that persisted would overwrite the panel's location.
   */
  persistState?: boolean
  /**
   * Whether the user has My Files. False for someone without a subscription who has folders shared
   * with them: the browser then offers the Shared root only. Defaults to true.
   */
  ownFiles?: boolean
}

/**
 * Whether the pick dialog may answer with a file from a folder someone else shares with the user.
 * Not yet: cloud runs refuse inputs outside the caller's own space until shared inputs land (shared-
 * folders plan P2-A1, P2-B2), so the job would only fail later. The desktop refuses the same way.
 * The owner's own shared folders open in My Files, so they are not affected.
 */
const PICK_FROM_FOLDERS_SHARED_WITH_ME = false

/** Said in the pick dialog when a file in a folder shared with the user is chosen. */
export const SHARED_INPUT_NOTE = "Files in folders shared with you can't be used as inputs yet."

/** A shared folder's name for messages: “Team data”. */
const shareDisplayName = (root: StorageRoot): string =>
  root.kind === 'share' ? `“${root.name || root.folderPath.replace(/\/$/, '').split('/').pop()}”` : ''

/** The folder an item is in, relative to its root: `a/b/c.txt` and `a/b/c/` are in `a/b`. */
const parentFolderOf = (path: string) => path.replace(/\/$/, '').split('/').slice(0, -1).join('/')

/** An item's name from its path: `a/b/c.txt` and `a/b/c/` are `c.txt` and `c`. */
const lastSegment = (path: string) => path.replace(/\/$/, '').split('/').pop() ?? path

/**
 * The selected items without those inside a selected folder: a folder is deleted with everything
 * in it, so its contents need not (and, past 100 paths, can't) be listed again.
 */
const topLevelPaths = (paths: string[]) =>
  paths.filter(
    (path) =>
      path.length > 0 &&
      !paths.some((other) => other !== path && other.endsWith('/') && path.startsWith(other))
  )

/** After a Move/Copy between My Files and a shared folder: refresh both sides (E8). */
function refreshAfterTransfer(
  queryClient: QueryClient,
  identityId: string | undefined,
  {intent, share, status}: ShareTransferOutcome
): void {
  void queryClient.invalidateQueries(fileQueryKeys.share(share.shareId))
  const ownFolders =
    intent.direction === 'toMine'
      ? [intent.destination]
      : intent.sources.map((source) => parentFolderOf(source.path))
  invalidateChangedFolders(queryClient, identityId, ownFolders)
  if (status === 'failed') {
    // Its owner may be moving it (409 share_moving): the list says so, and the browser follows.
    void queryClient.invalidateQueries(sharingQueryKeys.shares())
  }
}

/**
 * After the owner moved or renamed a shared folder (or a folder above one) in My Files: the folders
 * it left and the one it went to, and the shares, which now have a new `folderPath` (and perhaps a
 * new name). Done whether it worked or not: a failed move may have left copies behind.
 */
function refreshAfterShareMove(
  queryClient: QueryClient,
  identityId: string | undefined,
  {intent}: ShareMoveOutcome
): void {
  void queryClient.invalidateQueries(sharingQueryKeys.shares())
  const folders =
    intent.kind === 'rename'
      ? [parentFolderOf(intent.item.path)]
      : [...intent.items.map((item) => parentFolderOf(item.path)), intent.destination]
  invalidateChangedFolders(queryClient, identityId, folders)
}

/** The folders of My Files an owner's move or rename of a shared folder (or one above it) moved. */
function shareMoveFolderMoves(intent: ShareMoveIntent): FolderMove[] {
  if (intent.kind === 'rename') {
    const parent = parentFolderOf(intent.item.path)
    return renameMoves(intent.item.path, `${parent ? `${parent}/` : ''}${intent.newName}/`)
  }
  return intoFolderMoves(
    intent.items.map((item) => item.path),
    intent.destination
  )
}

function FileTableBrowser(props: FileTableProps) {
  const queryClient = useQueryClient()
  const {currentUser} = useCognitoAuth()
  const navigate = useNavigate()
  const layout = useLayoutPanelsOptional()
  const identityId = useRef<string>(currentUser?.identity_id || '')

  // Derived once and never re-derived inline, so `grep -n isPicker` is a complete audit of the mode.
  const isPicker = !!props.pick
  // The Files panel and the pick dialogs colour their rows in CSS (zebra, hover, selection: see
  // files-panel.scss and file-picker.scss); the full-page table keeps the inline accent border.
  const cssStyledRows = props.variant === 'panel' || isPicker
  const persistState = props.persistState ?? !isPicker
  const ownFiles = props.ownFiles ?? true

  /*
    Shared folders (plan P1-F3). Nothing about them shows unless the server says this user has
    sharing (`GET /v1/me/features`). Then the Shared virtual folder sits at the top of My Files, in
    the file-pick dialog too, so a wizard can take its inputs from a shared folder. `sharingKnown` is
    false until the answer is in, so a remembered shared folder is neither listed nor abandoned
    before then.
  */
  const {sharing, isLoading: sharingLoading} = useSharingAccess()
  const sharingKnown = !!currentUser?.sub && !sharingLoading
  const sharesState = useShares()
  // Without My Files (no subscription, or it lapsed) the folders this user shares live in storage
  // they can no longer browse, so the Shared root lists only folders shared with them.
  const listedShares = ownFiles ? sharesState.shares : sharesState.sharedWithMe
  // Pinned folders: for every signed-in user, not only the sharing pilot. Unavailable (no pins, no
  // Pin actions) while the endpoint answers nothing usable.
  const pinActions = usePinActions()

  /** Where the browser lands when it has to leave a shared folder: Home, or Shared without My Files. */
  const fallbackRoot = (): BrowserRoot => (ownFiles ? OWN_ROOT : SHARED_INDEX)

  /*
    The remembered location, `{root, folderPath}` (see fileBrowserLocation.ts), read once. Deep links
    name a folder in My Files; the pick dialog remembers nothing and opens at Home (or at
    `initialPath`).
  */
  const [storedLocation] = useState(() =>
    persistState && !props.initialPath ? readStoredLocation() : undefined
  )

  // My Files, the Shared virtual folder, or a shared folder; the folder inside it is `folderPath`.
  const [root, setRoot] = useState<BrowserRoot>(() => {
    if (isPicker) {
      return OWN_ROOT
    }
    const stored = storedLocation?.root ?? OWN_ROOT
    return stored.kind === 'own' ? fallbackRoot() : stored
  })
  // The root whose files are listed: none on the Shared root, which lists shares instead.
  const storageRoot = storageRootOf(root)
  const listingRoot: StorageRoot = storageRoot ?? OWN_ROOT
  // Storage calls go through `store`, and cached listings are named by fileQueryKeys, both for the
  // root on screen.
  const store = fileStoreFor(listingRoot, identityId.current)
  // The shared folder on screen, as the share list last described it.
  const shareOnScreen =
    root.kind === 'share'
      ? sharesState.shares.find((share) => share.shareId === root.id)
      : undefined
  // Its owner is moving it (P1-A14): reads go on, every write would answer 409 share_moving.
  const shareMoving = !!shareOnScreen?.moving
  // What can be done here: everything in My Files; in a folder shared with Can edit (P1-F4), files
  // but not imports; nothing in the Shared virtual folder, in a folder shared with Can view or in one
  // being moved, or in the pick dialog.
  const toolbar = useMemo(
    () => toolbarActions({picker: isPicker, root, moving: shareMoving}),
    [isPicker, root, shareMoving]
  )
  const canWrite = toolbar.newFolder.enabled
  // Renames and moves that carry one of the user's shared folders go through the sharing API.
  const openShareMove = useOpenShareMove()
  const registerFileImportActions = useRegisterFileImportActions()
  // The host builds `pick` inline, so hold the callbacks in a ref rather than letting a new object
  // identity invalidate every memo and effect that needs to reach them.
  const pickRef = useRef(props.pick)
  useEffect(() => {
    pickRef.current = props.pick
  }, [props.pick])

  const parseInitialPath = (pathStr: string): string[] => {
    const segments = pathStr.split('/').filter(Boolean)
    return segments.length > 0 ? ['', ...segments] : [ROOT_FOLDER]
  }

  // Initialize folder path from initialPath prop, then localStorage, then root. A remembered folder
  // of My Files is dropped when the browser opens elsewhere (Shared, without a subscription).
  const [folderPath, setFolderPath] = useState<string[]>(() => {
    if (root.kind === 'sharedIndex') {
      return TOP_FOLDER
    }
    if (props.initialPath) {
      return parseInitialPath(props.initialPath)
    }
    if (!storedLocation || storedLocation.root.kind !== root.kind) {
      return TOP_FOLDER
    }
    return storedLocation.folderPath
  })
  const folderPathRef = useRef<string[]>(folderPath)

  // Update ref when folderPath changes
  useEffect(() => {
    folderPathRef.current = folderPath
  }, [folderPath])

  // Notify the parent whenever the folder changes
  useEffect(() => {
    props.onFolderPathChange?.(folderPath, root)
  }, [folderPath, root]) // eslint-disable-line react-hooks/exhaustive-deps

  // Remember `{root, folderPath}`, so the location survives a reload.
  useEffect(() => {
    if (persistState) {
      storeLocation({root, folderPath})
    }
  }, [root, folderPath, persistState])

  // When initialPath prop changes — or the panel re-requests the same path via a bumped nonce —
  // update the folder path.
  const prevInitialPath = useRef(props.initialPath)
  const prevPathNonce = useRef(props.pathRequestNonce)
  useEffect(() => {
    const nonceChanged =
      props.pathRequestNonce !== undefined && props.pathRequestNonce !== prevPathNonce.current
    prevPathNonce.current = props.pathRequestNonce

    if (props.initialPath !== undefined && (props.initialPath !== prevInitialPath.current || nonceChanged)) {
      prevInitialPath.current = props.initialPath
      if (!ownFiles) {
        return
      }
      const newPath = parseInitialPath(props.initialPath)
      setRoot(OWN_ROOT)
      setFolderPath(newPath)
      folderPathRef.current = newPath
    }
  }, [props.initialPath, props.pathRequestNonce]) // eslint-disable-line react-hooks/exhaustive-deps

  // The listing on screen: the identity it is read as, and the folder relative to the root.
  const listing = useMemo(
    () => ({identityId: identityId.current, folder: folderPath.slice(1).join('/')}),
    // folderPath updates when auth resolves (copy in identity effect) so this picks up identityId
    [folderPath]
  )

  const {
    data: fileData,
    isError,
    error,
    isLoading,
    isFetching,
    isPreviousData,
    // Held off until Cognito resolves an identity. The identity effect below forces a re-render
    // when it does, so `listing` and this flag are both re-evaluated then.
    // A shared folder is read through the sharing API, as the signed-in user; the caller's
    // identityId only names the cache entry. Never listed while sharing is off or unknown.
  } = useFetchFiles(listingRoot, listing.identityId, listing.folder, {
    enabled: root.kind === 'own' ? !!identityId.current : root.kind === 'share' && sharing,
  })

  // Warm the parent folder cache whenever we're inside a subfolder so "go up" still has a cache hit
  // even if the default 5 min GC had been the only thing holding data (we now use FILES_LIST_GC_TIME_MS).
  useEffect(() => {
    const id = currentUser?.identity_id || identityId.current
    if (listingRoot.kind === 'own' ? !id || root.kind !== 'own' : !sharing) return
    const segments = folderPath.slice(1)
    if (segments.length === 0) return
    const parent = folderListQuery(listingRoot, id, segments.slice(0, -1).join('/'))
    const staleMs = currentUser?.identity_id ? 1000 * 60 * 2 : 0
    void queryClient.prefetchQuery(parent.queryKey, parent.queryFn, {
      staleTime: staleMs,
      cacheTime: FILES_LIST_GC_TIME_MS,
    })
  }, [root, listingRoot, sharing, folderPath, currentUser?.identity_id, queryClient])

  // keepPreviousData makes `fileData` the *previous* folder until the new request resolves.
  // The file tree must be built from the listing for the *current* path; prefer React Query
  // cache for this key (stale-while-revalidate) so we can keep showing the last known tree.
  const cachedFilesForPath = queryClient.getQueryData<dbItem[]>(
    fileQueryKeys.list(listingRoot, listing.identityId, listing.folder)
  )
  const fileDataForFileMap: dbItem[] | undefined = isPreviousData
    ? cachedFilesForPath
    : fileData

  const showTableSkeleton =
    fileDataForFileMap === undefined && (isLoading || (isPreviousData && isFetching))

  const {updateInfiniteToast} = useSocket([], [])
  const {fileMap} = useFileMap(fileDataForFileMap)

  // State persistence for sorting
  const getStoredSorting = (): SortingState => {
    if (!persistState) {
      return [{id: 'name', desc: false}]
    }
    try {
      const stored = localStorage.getItem('fileTable-sorting')
      if (stored) {
        const parsed = JSON.parse(stored)
        return Array.isArray(parsed) && parsed.length > 0 ? parsed : [{id: 'name', desc: false}]
      }
    } catch (error) {
      getLogger().error('Failed to parse stored sorting:', error)
    }
    return [{id: 'name', desc: false}]
  }

  const storeSorting = useCallback((sortingState: SortingState) => {
    if (!persistState) {
      return
    }
    try {
      localStorage.setItem('fileTable-sorting', JSON.stringify(sortingState))
    } catch (error) {
      getLogger().error('Failed to store sorting:', error)
    }
  }, [persistState])

  const [sorting, setSorting] = useState<SortingState>(() => getStoredSorting())

  // Store sorting in localStorage whenever it changes
  useEffect(() => {
    storeSorting(sorting)
  }, [sorting, storeSorting])

  const [files, setFiles] = useState<File[]>([])
  const [fileToRename, setFileToRename] = useState<CustomFileData>()
  const [showFolderModal, setShowFolderModal] = useState<boolean>(false)
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false)
  const [showImportWizard, setShowImportWizard] = useState<boolean>(false)
  const [importInitialSource, setImportInitialSource] = useState<ImportSourceType>('s3')
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false)

  const [previousRow, setPreviousRow] = useState<Row<CustomFileData> | null>()
  const [selectedRows, setSelectedRows] = useState<Row<CustomFileData>[]>([])
  const addSelectedRows = (rows: Row<CustomFileData>[]) =>
    setSelectedRows((selectedRows) => selectedRows.concat(rows))

  const deleteSelectedRow = (row: Row<CustomFileData>) =>
    setSelectedRows((selectedRows) => selectedRows.filter((r) => r.original.id !== row.original.id))

  const resetSelection = useCallback(
    (row?: Row<CustomFileData> | undefined) => {
      if (row) {
        if (selectedRows.length <= 1) {
          if (selectedRows.includes(row)) {
            deleteSelectedRow(row)
          } else {
            setSelectedRows([row])
          }
        } else {
          setSelectedRows([row])
        }
      } else {
        setSelectedRows([])
      }
    },
    [selectedRows]
  )
  const selectedFiles = useCallback(() => selectedRows.map((r) => r.original), [selectedRows])

  /*
    Compare by storage key, not by Row object identity. TanStack rebuilds every Row when the data
    changes, so `selectedRows.includes(row)` silently lost the highlight on any refetch — including
    the websocket-driven ones. Same object still implies same id, so this is a strict superset.
  */
  const isRowSelected = useCallback(
    (row: Row<CustomFileData>) => selectedRows.some((r) => r.original.id === row.original.id),
    [selectedRows]
  )

  // A file in a folder someone shares with the user is named by its share, so the host can build
  // its URI in the owner's space (`shareFileS3Url`). Not offered yet: see below.
  const pickedShare: ShareLocation | undefined =
    root.kind === 'share'
      ? {id: root.id, ownerIdentityId: root.ownerIdentityId, folderPath: root.folderPath}
      : undefined
  const pickedShareRef = useRef(pickedShare)
  pickedShareRef.current = pickedShare
  const toPicked = (file: CustomFileData): PickedFile => ({
    key: file.id,
    name: file.name,
    isDir: file.isDir,
    size: file.size ?? 0,
    ...(pickedShareRef.current ? {share: pickedShareRef.current} : {}),
  })

  // Pick mode inside a folder someone else shares: it can be browsed, but nothing in it can be the
  // answer yet (PICK_FROM_FOLDERS_SHARED_WITH_ME). Choosing there shows SHARED_INPUT_NOTE instead.
  const pickRefusedHere =
    isPicker &&
    !PICK_FROM_FOLDERS_SHARED_WITH_ME &&
    root.kind === 'share' &&
    root.access !== 'owner'
  const pickRefusedRef = useRef(pickRefusedHere)
  pickRefusedRef.current = pickRefusedHere
  const [pickRefused, setPickRefused] = useState(false)

  /**
   * Pick mode's single selection channel: highlight these rows and tell the host what is chosen.
   *
   * Every pick-mode selection change goes through here — a click, a preselect, and each of the resets
   * that navigating away performs. That is the point: with `onSelect` and `onSelectMany` reported from
   * one place, a reset can never clear one and leave the other holding a file from the folder the user
   * just left.
   *
   * Only choosable rows are reported. A folder is not one unless `selectFolders` says so, which is why
   * highlighting and answering are not the same thing here. Nothing in a folder someone else shares
   * with the user is choosable yet: the host hears "nothing chosen", and the dialog says why.
   */
  const reportSelection = useCallback((rows: Row<CustomFileData>[]) => {
    setSelectedRows(rows)

    const pick = pickRef.current
    if (!pick) {
      return
    }

    const wanted = rows
      .map((row) => row.original)
      .filter((file) => !file.isDir || !!pick.selectFolders)
    const refused = pickRefusedRef.current && wanted.length > 0
    setPickRefused(refused)
    const choosable = refused ? [] : wanted.map(toPicked)

    if (pick.multiple) {
      pick.onSelectMany?.(choosable)
    }

    // Called in both modes: a caller that reads only this keeps working when multiple is on.
    pick.onSelect(choosable[0])
  }, [])

  /** Pick mode: exactly one row highlighted. */
  const selectSingle = useCallback(
    (row: Row<CustomFileData>) => reportSelection([row]),
    [reportSelection]
  )
  const outerRef = useRef(null)
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)

  if (isError) {
    getLogger().error(error?.message)
  }
  const {onCopy, onCut, onPaste, setRenamedFile, renamedFile, countFiles, sendWsMessage} =
    useFileTable(selectedFiles(), undefined, listingRoot)

  // Read by the window-level shortcut handler, which is bound once and must not close over stale
  // props. Defaults to true so the standalone (non-panel) usage keeps working unchanged.
  const enabledRef = useRef(props.enabled ?? true)

  useEffect(() => {
    enabledRef.current = isPicker ? false : props.enabled ?? true
  }, [props.enabled, isPicker])
  /*
    Pinned folders are a sort order: a pinned folder is the same row, listed first in the folder that
    directly contains it (`pinned`, read by the sort and the name cell). A pin deeper down matches no
    row here; one whose folder is gone matches nothing at all.
  */
  const pinnedIds = useMemo(
    () =>
      storageRoot
        ? pinnedFolderIds(pinActions.pins, storageRoot, sharesState.sharedByMe)
        : NO_PINNED_IDS,
    [storageRoot, pinActions.pins, sharesState.sharedByMe]
  )

  const allEntries: CustomFileData[] = useMemo(() => {
    let folderContent: CustomFileData[] = []
    const folder =
      folderPath.length === 1 ? folderPath.join('/') : folderPath.slice(1).join('/').concat('/')
    fileMap[folder]?.children?.forEach((element) => {
      // A new object when pinned, so the table sorts again when pins change.
      folderContent.push(
        element.isDir && pinnedIds.has(element.id) ? {...element, pinned: true} : element
      )
    })
    return folderContent
  }, [fileMap, folderPath, pinnedIds])

  /** Pick mode only: "Show all files" defeats the parameter's extension filter. */
  const [showAllFiles, setShowAllFiles] = useState(false)
  const pickFilters = props.pick?.filters

  /*
    Filtering happens here rather than as a table column filter because the sorted row model, the
    folders-first sort and `open()`'s "find the row whose name matches the clicked text" lookup all
    read from this array — a display-layer filter would leave phantom rows behind in the row model.
    Folders always survive it, so a filter can never block navigation.
  */
  const currentFolder: CustomFileData[] = useMemo(() => {
    if (!isPicker || showAllFiles || !pickFilters?.length) {
      return allEntries
    }
    return allEntries.filter((entry) => entry.isDir || matchesFilters(entry.name, pickFilters))
  }, [allEntries, isPicker, showAllFiles, pickFilters])

  const hiddenByFilter = allEntries.length - currentFolder.length

  const folderStatus = useMemo(
    () => describeFolderStatus(currentFolder, selectedRows.map((r) => r.original)),
    [currentFolder, selectedRows]
  )

  function getChildren(childrenIds: string[] | undefined): CustomFileData[] {
    let children: CustomFileData[] = []
    childrenIds?.forEach((value) => children.push(fileMap[value]))

    return children
  }

  /**
   * The listing of the folder on screen, built when called: the mutations below read the folder,
   * and the identity unless they pass one, at that moment.
   */
  const shownListKey = useCallback(
    (identity: string = identityId.current) =>
      fileQueryKeys.list(listingRoot, identity, folderPathRef.current.slice(1).join('/')),
    [listingRoot]
  )

  const reload = useCallback(() => {
    if (root.kind === 'sharedIndex') {
      queryClient.invalidateQueries(sharingQueryKeys.shares())
      return
    }
    queryClient.invalidateQueries(shownListKey())
  }, [queryClient, shownListKey, root.kind])

  /** The Refresh button: at Home it reads the pins again too (the desktop may have changed them). */
  const refresh = useCallback(() => {
    reload()
    if (root.kind === 'own' && folderPathRef.current.length === 1) {
      void queryClient.invalidateQueries(pinsQueryKeys.all)
    }
  }, [reload, root.kind, queryClient])

  /** In My Files, the shares of the folders the user shares, by folder path (D9). */
  const sharedByFolder = useMemo(
    () => new Map(sharesState.sharedByMe.map((share) => [share.folderPath, share])),
    [sharesState.sharedByMe]
  )
  const sharedFolderFor = useCallback(
    (file: CustomFileData): ShareView | undefined =>
      root.kind === 'own' && file.isDir ? sharedByFolder.get(file.id) : undefined,
    [root.kind, sharedByFolder]
  )

  const {getHeaderGroups, getRowModel, getVisibleLeafColumns} = useReactTable({
    columns: columns,
    data: currentFolder,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      isFileOpenable: (file: CustomFileData) =>
        resolveOpenability(file.id, ontologyResolver.isOpenable(file.tags)),
      showShareIconOnName: props.variant === 'panel',
      sharedFolderFor,
    } as FileTableMeta,
    state: {
      sorting,
      columnVisibility: props.variant === 'panel' ? {shared: false} : {},
    },
    onSortingChange: setSorting,
    /*
      Key rows by path, not by array index (the default). With index ids, navigating into a folder
      handed the clicked folder's <tr> to whichever file of the new folder had the same index, and
      the double-click's trailing dblclick then opened that file.
    */
    getRowId: (row) => row.id,
    getSubRows: (row) => getChildren(row.childrenIds),
    getSortedRowModel: getSortedRowModel(),
    sortingFns: {
      foldersFirstSort: (
        rowA: Row<CustomFileData>,
        rowB: Row<CustomFileData>,
        columnId: string
      ): number => {
        // TanStack inverts the result for desc, so we compensate to keep these groups on top.
        const isDesc = sorting.find((s) => s.id === columnId)?.desc ?? false
        const firstWhen = (a: boolean, b: boolean) =>
          a === b ? 0 : a ? (isDesc ? 1 : -1) : isDesc ? -1 : 1

        // Pinned folders first, then the other folders, then files. Each group in the column's order.
        const pinned = firstWhen(!!rowA.original.pinned, !!rowB.original.pinned)
        if (pinned !== 0) {
          return pinned
        }
        const folders = firstWhen(rowA.original.isDir, rowB.original.isDir)
        if (folders !== 0) {
          return folders
        }

        const a = rowA.getValue(columnId)
        const b = rowB.getValue(columnId)

        if (typeof a === 'string' && typeof b === 'string') {
          return a.localeCompare(b, undefined, {numeric: true, sensitivity: 'base'})
        }

        if (a == null && b == null) return 0
        if (a == null) return -1
        if (b == null) return 1
        return a < b ? -1 : a > b ? 1 : 0
      },
    },
    enableSortingRemoval: false,
    enableMultiRowSelection: !isPicker,
    enableSubRowSelection: false,
  })

  /*
    Preselect the file the widget already holds, once its folder's listing has resolved. One-shot:
    `CustomFileData.id` is the full identity-relative key, so the row can only ever match inside the
    folder that owns it, and the ref makes it impossible to yank the selection back after the user
    has started navigating. A file that was deleted, or that the filter hides, simply stays
    unselected.
  */
  const didPreselect = useRef(false)
  useEffect(() => {
    if (!isPicker || didPreselect.current) {
      return
    }
    const key = props.pick?.initialSelectedKey
    if (!key) {
      didPreselect.current = true
      return
    }
    const row = getRowModel().rows.find((r) => r.original.id === key)
    if (row) {
      didPreselect.current = true
      selectSingle(row)
    }
  }, [isPicker, currentFolder, props.pick?.initialSelectedKey, selectSingle]) // eslint-disable-line react-hooks/exhaustive-deps

  const inputFile = useRef<HTMLInputElement | null>(null)
  const inputFolder = useRef<HTMLInputElement | null>(null)
  const onUploadFileClick = () => {
    if (inputFile !== null && inputFile.current !== null) inputFile.current.click()
  }
  const onUploadFolderClick = () => {
    if (inputFolder !== null && inputFolder.current !== null) inputFolder.current.click()
  }

  /** After a write in the shared folder on screen: refetch its listings (the socket may say so too). */
  const refreshShare = (shareRoot: StorageRoot = listingRoot) => {
    if (shareRoot.kind === 'share') {
      void queryClient.invalidateQueries(fileQueryKeys.share(shareRoot.id))
    }
  }

  /**
   * A write in a shared folder was refused as gone (404), not allowed (403), or paused while its
   * owner moves it (409 `share_moving`): refetch the share list. The browser then follows it (see
   * "Keep the root honest" below): it updates the access, says the folder is being moved, or leaves
   * a share that is gone.
   */
  const onShareWriteError = (error: unknown) => {
    if (isShareUnavailable(error) || shareErrorCode(error) === 'share_moving') {
      void queryClient.invalidateQueries(sharingQueryKeys.shares())
    }
  }

  /**
   * The storage API refused a rename or move in My Files because it carries a shared folder (409
   * `shared_folder_locked`): the share list here was out of date. Refetch it, and ask again through
   * the sharing API, whose dry run says which shares are involved. True when handled.
   */
  const rerouteSharedFolderMove = (error: unknown, intent: ShareMoveIntent): boolean => {
    if (listingRoot.kind !== 'own' || !isSharedFolderLocked(error)) {
      return false
    }
    void queryClient.invalidateQueries(sharingQueryKeys.shares())
    if (openShareMove) {
      openShareMove(intent)
    } else {
      toast.error(sharedFolderLockedMessage(error), toastActionableConfig)
    }
    return true
  }

  /** Uploads into the shared folder on screen (Can edit), followed in one toast with Cancel. */
  const uploadIntoShare = (items: ShareUploadItem[]) => {
    const shareRoot = listingRoot
    if (shareRoot.kind !== 'share') {
      return Promise.resolve()
    }
    return uploadBatchToShare(store, items, shareDisplayName(shareRoot)).then((result) => {
      result.failed.forEach(({error}) => onShareWriteError(error))
      refreshShare(shareRoot)
    })
  }

  /** Uploads files (and creates empty folders) at their paths relative to the folder shown. */
  function uploadDropped({files, emptyFolders}: DroppedContent): Promise<unknown> {
    const path = folderPathRef.current.slice(1, folderPathRef.current.length).join('/')
    const prefix = path === '' ? '' : `${path}/`
    if (listingRoot.kind === 'share') {
      // A shared folder with Can edit: the empty folders, then the files in one toast with Cancel,
      // both a few at a time like any upload.
      return runLimited(
        emptyFolders.map((folder) => () => store.createFolder(`${prefix}${folder}`))
      ).then(() =>
        uploadIntoShare(
          files.map(({file, relativePath}) => ({path: `${prefix}${relativePath}`, file}))
        )
      )
    }
    // A few at a time: starting hundreds of uploads at once stalls the page.
    const uploads = runLimited<unknown>([
      ...files.map(({file, relativePath}) => () => store.upload(`${prefix}${relativePath}`, file)),
      ...emptyFolders.map((folder) => () => store.createFolder(`${prefix}${folder}`)),
    ])
    sendWsMessage(
      'file-upload',
      path === '' ? `${identityId.current}/` : `${identityId.current}/${path}/`,
      files.length.toLocaleString(),
      undefined
    )
    return uploads
  }

  /** False (and a warning) when a selection, folder or drop holds more files than one upload may. */
  function withinUploadLimit(count: number): boolean {
    try {
      assertUploadSize(count)
      return true
    } catch (error: unknown) {
      if (!(error instanceof TooManyFilesError)) throw error
      toast.warning(error.message, toastDefaultConfig)
      return false
    }
  }

  function uploadFiles(files: File[]): Promise<unknown> {
    return uploadDropped({files: files.map((file) => ({file, relativePath: file.name})), emptyFolders: []})
  }

  const uploadFileMutation = useMutation(({files}: {files: File[]}) => uploadFiles(files), {
    onMutate: async () => {
      await queryClient.cancelQueries(shownListKey())
      const path = folderPathRef.current.slice(1, folderPathRef.current.length).join('/')
      const tempFiles: dbItem[] = files.map<dbItem>((f) => ({
        prefix: identityId.current,
        eventTime: convertTimestampToDateString(f.lastModified),
        size: f.size,
        file: path === '' ? `${f.name}` : `${path}/${f.name}`,
      }))
      const previousFiles = queryClient.getQueryData<dbItem[]>(shownListKey()) || []
      queryClient.setQueryData<dbItem[]>(shownListKey(), (currentFiles) =>
        currentFiles ? currentFiles.concat(tempFiles) : tempFiles
      )
      const rollback: () => void = () => queryClient.setQueryData(shownListKey(), previousFiles)
      return {rollback}
    },
    onError: (error: unknown, variables, context) => {
      context?.rollback()
      getLogger().error('Upload file error:', error)
      toast.error('Failed to upload file')
    },
    onSettled: () => {
      toast.clearWaitingQueue()
      toast.dismiss()
    },
  })

  const renameMutation = useMutation(
    // `props` are relative to the root, like every path the store takes.
    ({props, identityId}: {props: RenameProps; identityId: string}) =>
      fileStoreFor(listingRoot, identityId).rename(props.source, props.destination),
    {
      onMutate: async ({props, identityId}) => {
        await queryClient.cancelQueries(shownListKey(identityId))
        const previousFiles = queryClient.getQueryData<dbItem[]>(shownListKey(identityId))
        if (previousFiles) {
          queryClient.setQueryData<dbItem[]>(
            shownListKey(identityId),
            () =>
              previousFiles.map((file) => {
                if (file.file.includes(props.source)) {
                  const newFile = file.file.replace(props.source, props.destination)
                  return {...file, file: newFile}
                }
                return file
              })
          )
        }
        const rollback: () => void = () =>
          queryClient.setQueryData(shownListKey(identityId), previousFiles)
        // A shared folder renames in the background and the request waits for it: say so meanwhile.
        const shareRoot = listingRoot.kind === 'share' ? listingRoot : undefined
        const progressToast = shareRoot
          ? toast.loading('Renaming…', {...toastDefaultConfig, autoClose: false})
          : undefined
        return {rollback, shareRoot, progressToast}
      },
      onSuccess: (_data, {props}, context) => {
        setShowRenameModal(false)
        // A pinned folder (or one below it) keeps its pin under its new name.
        pinActions.followMoves(renameMoves(props.source, props.destination), context?.shareRoot?.id)
        if (context?.progressToast !== undefined) {
          toast.update(context.progressToast, {
            render: 'Renamed',
            type: 'success',
            isLoading: false,
            ...toastDefaultConfig,
          })
          refreshShare(context.shareRoot)
        } else {
          updateInfiniteToast(toast.loading('Renaming...', toastInfiniteConfig))
        }
        setRenamedFile('')
        setFileToRename(undefined)
      },
      onError: (error: unknown, {props}, context) => {
        context?.rollback()
        getLogger().error('Rename file error:', error)
        if (context?.progressToast !== undefined) {
          onShareWriteError(error)
          refreshShare(context.shareRoot)
          toast.update(context.progressToast, {
            render: `Couldn't rename: ${shareOperationErrorMessage(error)}`,
            type: 'error',
            isLoading: false,
            ...toastActionableConfig,
          })
          return
        }
        // E10: the share list was out of date, and this rename carries a shared folder.
        const rerouted = rerouteSharedFolderMove(error, {
          kind: 'rename',
          item: {
            path: props.source,
            name: lastSegment(props.source),
            isDir: props.source.endsWith('/'),
          },
          newName: lastSegment(props.destination),
        })
        if (rerouted) {
          return
        }
        toast.error('Failed to rename file')
      },
    }
  )

  function handleOnDelete(fileNames: string[]) {
    if (store.removeMany) {
      // A shared folder deletes in batches, a folder with everything in it.
      const paths = topLevelPaths(fileNames)
      return store.removeMany(paths).then(({failed}) => {
        if (failed.length > 0) {
          throw Object.assign(new Error('Some items could not be deleted.'), {failedPaths: failed})
        }
        return paths
      })
    }
    return Promise.all<Promise<string>[]>(
      fileNames.filter((v) => v.length > 0).map((fileName) => store.remove(fileName))
    )
  }
  const deleteFileMutation = useMutation((fileNames: string[]) => handleOnDelete(fileNames), {
    onMutate: async (fileNames) => {
      await queryClient.cancelQueries(shownListKey())
      const previousFiles = queryClient.getQueryData<dbItem[]>(shownListKey())
      if (previousFiles) {
        queryClient.setQueryData<dbItem[]>(shownListKey(), () =>
          previousFiles.filter((file) => !fileNames.includes(file.file))
        )
      }
      const rollback: () => void = () => queryClient.setQueryData(shownListKey(), previousFiles)
      return {rollback}
    },

    onError: (error: unknown, _2, context) => {
      context?.rollback()
      getLogger().error('Delete file error:', error)
      if (listingRoot.kind === 'share') {
        onShareWriteError(error)
        const failedPaths = (error as {failedPaths?: string[]}).failedPaths
        toast.error(
          failedPaths?.length
            ? `Couldn't delete ${failedPaths
                .map((p) => p.replace(/\/$/, '').split('/').pop())
                .join(', ')}.`
            : `Couldn't delete: ${shareOperationErrorMessage(error)}`,
          toastActionableConfig
        )
        return
      }
      toast.error('Failed to delete file')
    },
    onSettled: (_data, error, fileNames) => {
      if (listingRoot.kind === 'share') {
        if (!error) {
          toast('Files deleted', {...toastDefaultConfig, type: 'success'})
        }
        refreshShare()
      } else {
        toast('Files deleted', {...toastDefaultConfig, type: 'success'})
      }
      // Confirming already cleared the selection; by now the user may have selected other files,
      // so only drop the deleted ones.
      setSelectedRows((rows) => rows.filter((r) => !fileNames.includes(r.original.id)))
    },
  })

  function addNewFiles(newFiles: File[], files: File[], multiple: boolean): File[] {
    for (let file of newFiles) {
      if (!multiple) {
        return files.concat(file)
      }
      files.push(file)
    }
    return files
  }

  const handleNewFileUpload = (e: FileList | React.ChangeEvent<HTMLInputElement>) => {
    const newFiles = e instanceof FileList ? e : e.target.files

    if (newFiles && newFiles.length > 0) {
      if (!withinUploadLimit(newFiles.length)) {
        if (!(e instanceof FileList)) e.target.value = ''
        return
      }
      const fileArray = Array.from(newFiles)
      if (listingRoot.kind === 'share') {
        // One toast follows the whole batch, with its progress and a Cancel button.
        void uploadFiles(fileArray)
        return
      }
      const updatedFiles = addNewFiles(fileArray, files, true)
      setFiles(updatedFiles)
      uploadFileMutation.mutate({files: fileArray})
    }
  }

  const newFolderMutation = useMutation(
    (folderContent: FileList) => uploadFolder(store, folderContent, folderPathRef.current),
    {
      onMutate: async () => {
        await queryClient.cancelQueries(shownListKey())
        const previousFiles = queryClient.getQueryData<dbItem[]>(shownListKey())
        const rollback: () => void = () => queryClient.setQueryData(shownListKey(), previousFiles)
        return {rollback}
      },
      onSuccess: () => {
        updateInfiniteToast(toast.loading('Creating folder...', toastInfiniteConfig))
      },
      onError: (error: unknown, _, context) => {
        context?.rollback()
        getLogger().error('Upload folder error:', error)
        toast.error('Failed to upload folder')
      },
    }
  )

  const handleNewFolderUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const folderContent = e.target.files
    if (folderContent && folderContent.length > 0) {
      if (!withinUploadLimit(folderContent.length)) {
        e.target.value = ''
        return
      }
      if (listingRoot.kind === 'share') {
        // One toast follows the whole folder, with its progress and a Cancel button.
        const base = folderPathRef.current.slice(1).join('/')
        void uploadIntoShare(
          Array.from(folderContent).map((file) => ({
            path: base === '' ? file.webkitRelativePath : `${base}/${file.webkitRelativePath}`,
            file,
          }))
        )
        return
      }
      const path =
        folderPathRef.current.length > 1
          ? `${identityId.current}/${folderPathRef.current
              .slice(1, folderPathRef.current.length)
              .join('/')}`
          : `${identityId.current}`
      sendWsMessage('folder-upload', path, folderContent.length.toLocaleString(), undefined)
      newFolderMutation.mutate(folderContent)
    }
  }

  const openNewFolderModal = () => {
    setShowFolderModal(true)
  }
  const closeNewFolderModal = () => {
    reset()
    setShowFolderModal(false)
  }

  const createFolderMutation = useMutation((folderName: string) => store.createFolder(folderName), {
    onMutate: async () => {
      await queryClient.cancelQueries(shownListKey())
      const previousFiles = queryClient.getQueryData<dbItem[]>(shownListKey())
      const rollback: () => void = () => queryClient.setQueryData(shownListKey(), previousFiles)
      return {rollback}
    },
    onSuccess: () => {
      if (listingRoot.kind === 'share') {
        toast('Folder created', {...toastDefaultConfig, type: 'success'})
        refreshShare()
        return
      }
      updateInfiniteToast(toast.loading('Creating folder...', toastInfiniteConfig))
    },
    onError: (error: unknown, _, context) => {
      context?.rollback()
      getLogger().error('Create folder error:', error)
      if (listingRoot.kind === 'share') {
        onShareWriteError(error)
        toast.error(`Couldn't create the folder: ${shareOperationErrorMessage(error)}`)
        return
      }
      toast.error('Failed to create folder')
    },
  })

  /** `newFolderName` is checked and trimmed by ItemNameModal (checkItemName). */
  const handleCreateFolder = (newFolderName: string) => {
    const basePath =
      folderPathRef.current.length > 1
        ? folderPathRef.current.slice(1, folderPathRef.current.length).join('/')
        : ''
    if (listingRoot.kind === 'own') {
      sendWsMessage(
        'folder-create',
        `${identityId.current}${basePath === '' ? '' : `/${basePath}`}/${newFolderName}`,
        '1',
        undefined
      )
    }
    const folderName = basePath === '' ? `${newFolderName}` : `${basePath}/${newFolderName}`
    createFolderMutation.mutate(folderName)
    closeNewFolderModal()
    reset()
  }

  const closeRenameModal = () => {
    reset()
    setShowRenameModal(false)
  }

  /** `newName` is checked and trimmed by ItemNameModal (checkItemName). */
  const handleRenameSubmit = (newName: string) => {
    if (fileToRename) {
      const source = fileToRename.id
      // E10: a folder the user shares, or one above it, is renamed through the sharing API and the
      // share follows, once the user confirms.
      if (
        listingRoot.kind === 'own' &&
        openShareMove &&
        newName &&
        movesSharedFolder([source], sharesState.sharedByMe)
      ) {
        openShareMove({
          kind: 'rename',
          item: {path: source, name: fileToRename.name, isDir: fileToRename.isDir},
          newName,
        })
        setShowRenameModal(false)
        setRenamedFile('')
        setFileToRename(undefined)
        return
      }
      // Relative to the root, as the store takes it. The notification still sends the full key.
      let destination: string = folderPathRef.current.slice(1).concat(newName).join('/')
      if (fileToRename.isDir) destination = `${destination}/`
      if (destination) {
        renameMutation.mutate({props: {destination, source}, identityId: identityId.current})
        if (listingRoot.kind !== 'own') {
          return
        }
        const fileCount =
          fileToRename.children && fileToRename.children.length > 0
            ? countFiles(fileToRename.children) + 1 + '' // +1 for the folder itself
            : '1'
        sendWsMessage('rename', `${identityId.current}/${destination}`, fileCount, {
          fileName: fileToRename.name,
        })
      }
    }
  }

  const open = async (e: SyntheticEvent, row?: Row<CustomFileData>) => {
    e.stopPropagation()
    // The clicked row when the caller has it. Otherwise found by the name cell's text, which only
    // works while that cell holds nothing but the name (a shared folder's "Shared with N" label
    // is in it too).
    let fileData = row?.original
    if (!fileData) {
      const target = e.currentTarget as HTMLElement
      const folderSelected = target.childNodes[0]?.textContent
      if (!folderSelected) {
        return
      }
      fileData = getRowModel().rows.find((value) => value.original.name === folderSelected)?.original
    }

    if (!fileData) {
      return
    }

    if (fileData.isDir && fileData.id) {
      // opens folder
      const filePathArray = fileData.id.split('/').filter((folder) => folder !== '')
      resetSelection()
      // Navigating away must retract the answer: otherwise Select would still submit a file from
      // the folder the user just left. Both selection channels, via the one reporter.
      reportSelection([])
      filePathArray.unshift('')
      setFolderPath(filePathArray)
      folderPathRef.current = filePathArray
    }
    // Files are opened by onRowDoubleClick (metadata-driven viewer routing) — this used to also
    // download + window.open the raw file here, firing alongside the real viewer on every
    // double-click regardless of whether the file had a working viewer.
  }

  const {show: showContextMenu, hideAll: hideContextMenu} = useContextMenu({
    id: MENU_ID,
  })

  const {resolver: ontologyResolver} = useOntologyTree()

  /** The row that received the second click of the current double-click, set in onRowClick. */
  const secondClickRowIdRef = useRef<string | null>(null)

  const onRowDoubleClick = async (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: Row<CustomFileData>
  ) => {
    hideContextMenu()
    e.stopPropagation()

    // Pick mode handles both clicks of a double-click in onRowClick (via `detail`). Letting this
    // run would call openFileInViewer, which navigates the whole app out from under the dialog.
    if (isPicker) {
      return
    }

    // Only process files, not folders
    if (row.original.isDir) {
      return
    }

    /*
      A dblclick always follows a second click (detail 2). If that click went to another row — a
      folder it navigated into — the table has already re-rendered, and this dblclick is landing on
      a file of the new folder the user never clicked.
    */
    const secondClickRowId = secondClickRowIdRef.current
    secondClickRowIdRef.current = null
    if (secondClickRowId !== row.original.id) {
      return
    }

    // Skip files we know have no web viewer: the ontology tree says the file's
    // format (and every ancestor) is unsupported. Avoids a wasted /metadata
    // round-trip that would only end in a 404/"viewer unavailable". Unknown
    // tags fall through and still attempt to open (no regression).
    if (resolveOpenability(row.original.id, ontologyResolver.isOpenable(row.original.tags)) === false) {
      toast.info('This file type cannot be opened in a viewer yet.', toastDefaultConfig)
      return
    }

    // Optimistic open with instant loading placeholder + ontology-driven metadata resolution.
    // Shared with viewer-opening context-menu actions (see open-in-viewer / file-table-actions).
    await openFileInViewer({
      ...viewerTarget(storageRoot, row.original.id),
      displayName: row.original.name,
      tags: row.original.tags,
      navigate,
      expandCenter: layout?.expandCenter,
    })
  }

  const onRowClick = (
    e: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
    row: Row<CustomFileData>
  ) => {
    hideContextMenu()
    e.stopPropagation()
    const {ctrlKey, metaKey, shiftKey, detail} = e
    if (detail === 2) {
      secondClickRowIdRef.current = row.original.id
    }

    /*
      Pick mode: single selection, and the second click of a double-click either navigates into a
      folder or answers with the file. React fires onClick for both clicks (detail 1 then 2), so
      click #1 highlights and click #2 acts — the same feel the standalone picker had. onRowDouble-
      Click is short-circuited above, so nothing races this.
    */
    if (isPicker) {
      const multiple = !!pickRef.current?.multiple
      setPreviousRow(row)

      if (detail >= 2) {
        if (row.original.isDir) {
          open(e, row)
          return
        }
        /*
          In multiple mode a double-click must not answer: the dialog would close while the user is
          still choosing. Both clicks fall through to the toggle below, so it reads as one click.
        */
        if (!multiple) {
          if (pickRefusedRef.current) {
            // Not an input yet: highlight it and say why, rather than answer.
            selectSingle(row)
          } else {
            pickRef.current?.onConfirm(toPicked(row.original))
          }
          return
        }
      }

      if (multiple && (ctrlKey || metaKey)) {
        // The same gesture the Files panel uses, so there is nothing new to learn.
        reportSelection(
          isRowSelected(row)
            ? selectedRows.filter((r) => r.original.id !== row.original.id)
            : selectedRows.concat(row)
        )
        return
      }

      if (multiple && shiftKey && previousRow) {
        const rows = getRowModel().rows
        const to = rows.findIndex((r) => r.id === row.id)
        const from = rows.findIndex((r) => r.id === previousRow.id)
        if (to >= 0 && from >= 0) {
          reportSelection(rows.slice(Math.min(from, to), Math.max(from, to) + 1))
          return
        }
      }

      selectSingle(row)
      return
    }

    const isSelected = isRowSelected(row)
    if (ctrlKey || metaKey) {
      isSelected ? deleteSelectedRow(row) : addSelectedRows([row])
      setPreviousRow(row)
    } else if (shiftKey && previousRow) {
      const rows = getRowModel().rows
      const lastIndex = rows.findIndex((r) => r.id === row.id)
      const index = rows.findIndex((r) => r.id === previousRow.id)
      const min = Math.min(lastIndex, index)
      const max = Math.max(lastIndex, index)
      resetSelection()
      setSelectedRows(rows.slice(min, max + 1))
    } else {
      setPreviousRow(row)
      detail === 1 ? resetSelection(row) : open(e, row)
    }
    selectedRows.forEach((r) => r.toggleSelected())
  }

  const onAuxClick = (row: Row<CustomFileData>) => {
    if (!isRowSelected(row)) {
      setSelectedRows([row])
    }
    setPreviousRow(row)
  }

  const onDropEvent = (drop: DroppedEntries) => {
    if (!canWrite) {
      return
    }
    // Dropped folders are walked into their files, keeping the folder structure.
    collectDroppedFiles(drop)
      .then(uploadDropped)
      .catch((error: unknown) => {
        if (error instanceof TooManyFilesError) {
          toast.warning(error.message, toastDefaultConfig)
          return
        }
        getLogger().error('Drop upload error:', error)
        toast.error('Failed to upload the dropped files')
      })
  }

  const folderInputAttr = {directory: '', webkitdirectory: ''}

  /*
    Whether the user is working in this browser, going by their last click or focus. The shortcut
    listener is bound to `window` and the lateral panel keeps FileTable mounted on every route, so
    without this a Ctrl+A or Ctrl+C in the AI chat or a viewer would select or copy files instead
    of text, and a Ctrl+V would paste clipboard files into the last-visited folder.
  */
  const rootRef = useRef<HTMLDivElement>(null)
  const activeRef = useRef(false)
  useEffect(() => {
    if (isPicker) {
      return
    }
    const track = (ev: Event) => {
      activeRef.current = !!rootRef.current?.contains(ev.target as Node)
    }
    window.addEventListener('pointerdown', track, true)
    window.addEventListener('focusin', track, true)
    return () => {
      window.removeEventListener('pointerdown', track, true)
      window.removeEventListener('focusin', track, true)
    }
  }, [isPicker])

  // Reassigned every render so the listener, bound once, always sees the current rows and selection.
  const runShortcutRef = useRef<(key: string) => boolean>(() => false)
  runShortcutRef.current = (key: string) => {
    if (root.kind === 'sharedIndex') {
      return false
    }
    // Copy, cut and paste change storage: not offered where the user cannot write.
    if (!canWrite && key !== 'a') {
      return false
    }
    switch (key) {
      case 'a':
        setSelectedRows(getRowModel().rows)
        return true
      case 'c':
        onCopy()
        return true
      case 'x':
        // A folder the user shares (or one above it) may be cut too: the paste asks first (E10).
        onCut()
        return true
      case 'v':
        onPaste(folderPathRef.current)
        resetSelection()
        return true
      default:
        return false
    }
  }

  // Ctrl on Windows/Linux, Cmd on macOS.
  const handleUserKeyPress = useCallback((ev: KeyboardEvent) => {
    if (!(ev.ctrlKey || ev.metaKey) || ev.shiftKey || ev.altKey) {
      return
    }
    if (!enabledRef.current || !activeRef.current) {
      return
    }
    const target = ev.target as HTMLElement | null
    if (target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) {
      return
    }
    if (runShortcutRef.current(ev.key.toLowerCase())) {
      ev.preventDefault()
    }
  }, [])

  useEffect(() => {
    // A dialog must never register window-level shortcuts: it would compete with the panel's, arm
    // the clipboard the file manager later pastes from, and paste into whatever folder it shows.
    if (isPicker) {
      return
    }
    window.addEventListener('keydown', handleUserKeyPress)

    return () => {
      window.removeEventListener('keydown', handleUserKeyPress)
    }
  }, [handleUserKeyPress, isPicker])

  const handleDragStart = (e: React.DragEvent, fileData: CustomFileData) => {
    // When multiple rows are selected and the dragged row is in the selection, drag all selected
    const dragSources = selectedRows.some((r) => r.original.id === fileData.id) && selectedRows.length > 1
      ? selectedRows.map((r) => r.original)
      : [fileData]

    const toDragData = (f: CustomFileData): DragFileData => {
      const base = {type: f.isDir ? ('folder' as const) : ('file' as const), name: f.name, isDir: f.isDir}
      if (storageRoot?.kind === 'share') {
        if (storageRoot.access === 'owner') {
          // The owner's own folder: the same path My Files would give it.
          return {...base, id: `${storageRoot.folderPath}${f.id}`, s3Prefix: shareObjectKey(storageRoot, f.id)}
        }
        // Someone else's folder. The chat refuses these for now (P1-F6) and says why.
        return {...base, id: f.id, s3Prefix: shareObjectKey(storageRoot, f.id), shareId: storageRoot.id}
      }
      const s3KeyWithIdentity = `${identityId.current}/${f.id}`
      return {...base, id: f.id, s3Prefix: s3KeyWithIdentity}
    }

    const dragPayload = dragSources.length === 1
      ? toDragData(dragSources[0])
      : dragSources.map(toDragData)

    e.dataTransfer.setData('application/json', JSON.stringify(dragPayload))
    e.dataTransfer.effectAllowed = 'copy'

    // Drag a compact chip (the tag it becomes in the chat) instead of the whole row
    dragPreviewRef.current = setDragTagPreview(
      e,
      dragSources.length > 1 ? `${dragSources.length} items` : fileData.name,
      fileData.isDir ? 'folder' : 'file',
    )
  }

  const handleDragEnd = () => {
    removeDragTagPreview(dragPreviewRef.current)
    dragPreviewRef.current = null
  }

  const reset = () => {
    setPreviousRow(undefined)
    resetSelection()
  }

  // Clear stored state when user changes (logout / different account)
  useEffect(() => {
    const currentIdentityId = currentUser?.identity_id
    if (currentIdentityId && currentIdentityId !== identityId.current) {
      const isInitialLoad = identityId.current === ''
      identityId.current = currentIdentityId
      if (isInitialLoad) {
        // Auth just resolved after mount — keep stored folder path but re-render
        // so useFetchFiles picks up the correct identityId
        setFolderPath((prev) => [...prev])
      } else {
        if (persistState) {
          clearUserSessionLocalStorage()
        }
        setRoot(fallbackRoot())
        setFolderPath([ROOT_FOLDER])
        setSorting([])
      }
    }
  }, [currentUser?.identity_id, persistState]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Go to a folder of a root (its top by default), dropping the selection made elsewhere. */
  const goTo = useCallback(
    (nextRoot: BrowserRoot, path: string[] = [ROOT_FOLDER]) => {
      setSelectedRows([])
      setPreviousRow(undefined)
      reportSelection([])
      setRoot(nextRoot)
      setFolderPath(path)
      folderPathRef.current = path
    },
    [reportSelection]
  )

  // The starting root is chosen once, at mount. My Files can be taken away later — subscription
  // details arrive after mount and say the subscription lapsed, or it lapses while open — so leave
  // it for Shared then instead of listing storage the user no longer has.
  useEffect(() => {
    if (!ownFiles && !isPicker && root.kind === 'own') {
      goTo(SHARED_INDEX)
    }
  }, [ownFiles]) // eslint-disable-line react-hooks/exhaustive-deps

  /** Open an entry of the Shared root: browse it, or for the owner, show it where it lives. */
  const openShare = useCallback(
    (share: ShareView) => {
      if (share.access === 'owner') {
        goTo(OWN_ROOT, [ROOT_FOLDER, ...share.folderPath.split('/').filter(Boolean)])
      } else {
        goTo(shareRootFromView(share))
      }
    },
    [goTo]
  )

  /*
    Keep the root honest. Sharing off (or switched off) means My Files; a shared folder the share
    list no longer has is gone for this user; one whose access or name changed is refreshed in place.
    Acts only on a successful list, so a failed request never throws the user out of a folder.
  */
  useEffect(() => {
    if (root.kind === 'own' || !sharingKnown) {
      return
    }
    if (!sharing) {
      if (ownFiles) {
        goTo(OWN_ROOT)
      }
      return
    }
    if (root.kind !== 'share' || !sharesState.isLoaded) {
      return
    }
    const view = sharesState.shares.find((share) => share.shareId === root.id)
    if (!view) {
      toast.warning('That shared folder is no longer available to you.', toastDefaultConfig)
      goTo(fallbackRoot())
      return
    }
    if (view.access === 'owner') {
      openShare(view)
      return
    }
    const fresh = shareRootFromView(view)
    if (
      fresh.access !== root.access ||
      fresh.name !== root.name ||
      fresh.folderPath !== root.folderPath ||
      fresh.ownerIdentityId !== root.ownerIdentityId
    ) {
      setRoot(fresh)
    }
  }, [root, sharing, sharingKnown, sharesState.isLoaded, sharesState.shares]) // eslint-disable-line react-hooks/exhaustive-deps

  /*
    The server says the shared folder on screen is gone (404) or no longer readable (403): go back to
    My Files, then drop that share's listings and presigned URLs, and nothing else. The drop waits
    for the move, so the listing being dropped is no longer the one on screen (dropping it while
    observed would only refetch it).
  */
  const [goneShareId, setGoneShareId] = useState<string | null>(null)
  useEffect(() => {
    if (root.kind === 'share' && isError && isShareUnavailable(error)) {
      toast.warning('That shared folder is no longer available to you.', toastDefaultConfig)
      setGoneShareId(root.id)
      goTo(fallbackRoot())
    }
  }, [isError, error, root]) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (goneShareId && !(root.kind === 'share' && root.id === goneShareId)) {
      dropShareCaches(queryClient, goneShareId)
      setGoneShareId(null)
    }
  }, [goneShareId, root, queryClient])

  // The notification socket says a share is gone for this user (`share.removed`, `share.stopped`):
  // leave it if it is on screen, then drop its listing (above).
  useEffect(
    () =>
      subscribeShareGone((detail) => {
        if (root.kind === 'share' && root.id === detail.shareId) {
          toast.warning(shareGoneMessage(detail), toastDefaultConfig)
          setGoneShareId(detail.shareId)
          goTo(fallbackRoot())
        }
      }),
    [root, goTo] // eslint-disable-line react-hooks/exhaustive-deps
  )

  /** Where the browser is, for the breadcrumb: Home › a, Home › Shared, Home › Shared › share › a. */
  const location = useMemo<LocationView>(
    () => ({
      kind: root.kind,
      shareName:
        root.kind === 'share' ? root.name || root.folderPath.replace(/\/$/, '') : undefined,
      showHome: ownFiles,
    }),
    [root, ownFiles]
  )
  const goHome = useCallback(() => {
    if (ownFiles) {
      goTo(OWN_ROOT)
    }
  }, [ownFiles, goTo])
  const goShared = useCallback(() => goTo(SHARED_INDEX), [goTo])

  /*
    The rows that stand for shared folders but are not files: the Shared row at the top of My Files
    and the rows of the Shared list. They open on a double-click like folders, and have their own
    menu (Open, Pin to top / Unpin). The pick dialog has no menus.
  */
  const showSharedRow = sharing && root.kind === 'own' && folderPath.length === 1
  const sharedPinnedIds = useMemo(
    () => pinnedShareIds(pinActions.pins, sharesState.shares),
    [pinActions.pins, sharesState.shares]
  )
  const {show: showSharedRowMenu} = useContextMenu({id: SHARED_ROW_MENU_ID})
  const sharedRowMenu = (e: React.MouseEvent, target: SharedRowMenuTarget) => {
    hideContextMenu()
    showSharedRowMenu(e, {props: target})
  }
  const onSharedRootContextMenu = isPicker
    ? undefined
    : (e: React.MouseEvent) => sharedRowMenu(e, {open: () => goTo(SHARED_INDEX), candidates: []})
  const onSharedFolderContextMenu = isPicker
    ? undefined
    : (e: React.MouseEvent, share: ShareView) =>
        sharedRowMenu(e, {open: () => openShare(share), candidates: sharePinCandidates(share)})

  /*
    Imports, and uploads asked for from outside the browser (the chat composer's Import Data), go to
    My Files. Asked for while a shared folder or the Shared root is on screen, the browser moves to
    My Files first. The browser's own Upload buttons upload into a shared folder with Can edit.
  */
  const ownFilesActions = useMemo(() => {
    const inOwnFiles =
      <A extends unknown[]>(action: (...args: A) => void) =>
      (...args: A) => {
        if (root.kind !== 'own') {
          if (!ownFiles) {
            return
          }
          goTo(OWN_ROOT)
        }
        action(...args)
      }
    return {
      onUploadFile: isPicker ? () => {} : inOwnFiles(onUploadFileClick),
      onUploadFolder: isPicker ? () => {} : inOwnFiles(onUploadFolderClick),
      onImportS3: isPicker
        ? () => {}
        : inOwnFiles((sourceType?: ImportSourceType) => {
            setImportInitialSource(sourceType || 's3')
            setShowImportWizard(true)
          }),
    }
  }, [root.kind, ownFiles, goTo, isPicker]) // eslint-disable-line react-hooks/exhaustive-deps

  const fileTableProviderValues = useMemo(() => {
    return {
      resetSelection,
      setShowDeleteConfirmation,
      setShowRenameModal,
      showDeleteConfirmation,
      showRenameModal,
      selectedFiles,
      folderPathRef,
      folderPath,
      // By position: a folder name can repeat along the path (`a/x/b/x`), so its name cannot say
      // which crumb was clicked.
      onOpenFolder:
        ownFiles && !isPicker
          ? (relativePath: string) =>
              goTo(OWN_ROOT, [ROOT_FOLDER, ...relativePath.split('/').filter((part) => part !== '')])
          : undefined,
      onBreadCrumb: (index: number) => {
        const pathToTarget = folderPathRef.current.slice(0, Math.max(index, 0) + 1)
        resetSelection()
        reportSelection([])
        setFolderPath(pathToTarget)
        folderPathRef.current = pathToTarget
        reload()
      },
      onNewFolder: canWrite ? openNewFolderModal : () => {},
      onReturn: () => {
        if (folderPathRef.current.length > 1) {
          folderPathRef.current.pop()
          const path = [...folderPathRef.current]
          setFolderPath(path)
          folderPathRef.current = path
          resetSelection()
          reportSelection([])
          reload()
        }
      },
      // Into the folder on screen when it can be written to; otherwise into My Files.
      onUploadFile: canWrite ? onUploadFileClick : ownFilesActions.onUploadFile,
      onUploadFolder: canWrite ? onUploadFolderClick : ownFilesActions.onUploadFolder,
      onImportS3: ownFilesActions.onImportS3,
      onRefresh: refresh,
      fileToRename: fileToRename,
      setFileToRename: setFileToRename,
      onRename: !canWrite
        ? () => {}
        : () => {
            setFileToRename(selectedFiles()[0])
            if (selectedFiles().length === 1)
              setRenamedFile(selectedFiles().map((v) => v.name)[0] || '')
          },
      isRefetching:
        root.kind === 'sharedIndex' ? sharesState.isFetching : isFetching && !isLoading,
      readOnly: root.kind !== 'own' && !canWrite,
      ownFiles,
      location,
      onGoHome: goHome,
      onGoShared: goShared,
      toolbar,
      storageRoot: storageRoot ?? undefined,
    }
  }, [
    fileToRename,
    folderPath,
    setRenamedFile,
    resetSelection,
    reload,
    selectedFiles,
    showDeleteConfirmation,
    showRenameModal,
    isFetching,
    isLoading,
    isPicker,
    reportSelection,
    root.kind,
    location,
    goHome,
    goShared,
    toolbar,
    refresh,
    storageRoot,
    sharesState.isFetching,
    ownFiles,
    goTo,
    canWrite,
    ownFilesActions,
  ])

  useEffect(() => {
    // Without My Files there is nowhere to upload to.
    if (isPicker || props.variant !== 'panel' || !ownFiles) {
      return
    }
    registerFileImportActions(ownFilesActions)
    return () => registerFileImportActions(null)
  }, [isPicker, props.variant, ownFiles, registerFileImportActions, ownFilesActions])

  const newFolderModal = (
    <ItemNameModal
      key='new-folder-modal'
      title='New folder'
      titleIcon='fas fa-folder-plus'
      label='Folder name'
      submitText='Create'
      inputId='new-folder-input'
      placeholder='Untitled folder'
      onSubmit={handleCreateFolder}
      onClose={closeNewFolderModal}
    />
  )
  const renameModal = (
    <ItemNameModal
      key='rename-modal'
      title='Rename'
      titleIcon='fas fa-pen'
      label='New name'
      submitText='Rename'
      inputId='rename-input'
      initialValue={renamedFile}
      onSubmit={(newName) => {
        handleRenameSubmit(newName)
        closeRenameModal()
      }}
      onClose={closeRenameModal}
    />
  )
  const confirmDelete = () => {
    deleteFileMutation.mutate(findIds(selectedFiles()))
    setShowDeleteConfirmation(false)
    reset()
  }
  const cancelDelete = () => {
    setShowDeleteConfirmation(false)
    reset()
  }
  const deleteConfirmationModal = (
    <CustomModal
      key={'delete-confirmation-modal'}
      // Kept alongside `buttons` so Enter still confirms.
      submit={confirmDelete}
      closeModal={cancelDelete}
      title={'Confirm delete?'}
      titleIcon='fas fa-trash-can'
      buttons={[
        {text: 'Cancel', className: 'btn btn-light', onClick: cancelDelete},
        {text: 'Delete', className: 'btn btn-danger', onClick: confirmDelete},
      ]}
    >
      <>
        <p>The following files will be deleted:</p>
        <ul className='mb-0'>
          {selectedFiles().map((file) => (
            <li key={file.id}>{file.name}</li>
          ))}
        </ul>
        {/* E10: deleting a folder the user shares, or one above it, takes it from its members. */}
        {root.kind === 'own' &&
          sharesState.sharedByMe
            .filter((share) =>
              selectedFiles().some((file) => file.isDir && share.folderPath.startsWith(file.id))
            )
            .map((share) => (
              <p
                key={share.shareId}
                className='text-danger mt-3 mb-0'
                data-testid='delete-shared-folder-warning'
              >
                “{share.name || share.folderPath.replace(/\/$/, '')}” is shared with{' '}
                {share.memberCount === 1 ? '1 person' : `${share.memberCount} people`}. Its files
                will be deleted for them too.
              </p>
            ))}
      </>
    </CustomModal>
  )
  return (
    <>
      {!isPicker && showFolderModal && newFolderModal}
      {!isPicker && showRenameModal && renameModal}
      {!isPicker && showDeleteConfirmation && deleteConfirmationModal}
      {!isPicker && showImportWizard && (
        <ImportWizard
          destinationPrefix={identityId.current + '/' + folderPathRef.current.filter(Boolean).join('/') + '/'}
          identityId={identityId.current}
          initialSourceType={importInitialSource}
          onClose={() => setShowImportWizard(false)}
          onComplete={() => {
            setShowImportWizard(false)
            reload()
          }}
        />
      )}
      {/* Not rendered in pick mode: upload is off, and a second copy would duplicate these ids. */}
      {!isPicker && (
        <>
          <input
            type='file'
            multiple
            id='file-upload'
            data-testid='omics-files-upload-input'
            ref={inputFile}
            style={{display: 'none'}}
            onChange={handleNewFileUpload}
          />
          <input
            type='file'
            id='folder-upload'
            data-testid='omics-files-folder-input'
            ref={inputFolder}
            style={{display: 'none'}}
            onChange={handleNewFolderUpload}
            {...folderInputAttr}
          />
        </>
      )}

      <FileTableContext.Provider value={fileTableProviderValues}>
        {/* Marks the browser's extent for the shortcut scoping; `contents` keeps it out of layout. */}
        <div ref={rootRef} style={{display: 'contents'}}>
        <FileTableHeader
          title={props.title}
          hideTitle={true}
          collapseProps={props.collapseProps}
          variant={isPicker ? 'picker' : props.variant}
          onClose={props.onClose}
        />
        {shareMoving && root.kind === 'share' && (
          <div
            className='alert alert-info small py-2 px-3 ob-share-moving-notice'
            role='status'
            data-testid='share-moving-notice'
          >
            <i className='fas fa-lock me-2' aria-hidden='true' />
            {shareDisplayName(root)} is being moved by its owner. You can open files; changes are
            paused until it's done.
          </div>
        )}
        <div
          // Renamed in pick mode so the #context-menu-container rules in custom.scss and
          // files-panel.scss keep matching only the panel, and the two ids never collide.
          id={isPicker ? 'ob-file-pick-container' : 'context-menu-container'}
          onClick={
            isPicker
              ? undefined
              : (e) => {
                  const target = e.target as HTMLElement
                  const offsetParent = target.offsetParent as HTMLElement
                  if (offsetParent && !offsetParent.id.includes('context')) {
                    reset()
                  }
                }
          }
          onContextMenu={
            isPicker || root.kind === 'sharedIndex'
              ? undefined
              : (e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  if (e.shiftKey === false) {
                    showContextMenu(e)
                  }
                }
          }
          style={{
            height: 'var(--ob-file-table-height, 60vh)',
            width: 'auto',
            // minHeight: 'var(--ob-spacing-80, 400px)',   // pre-panel: --ob-spacing-80 is defined
            // nowhere, so this always resolved to the 400px fallback. Swapped to the real (and
            // previously unused) var from custom.scss:116 so the lateral panel can override it.
            // Same 400px default => page rendering unchanged.
            minHeight: 'var(--ob-file-table-min-height, 400px)',
            maxHeight: 'var(--ob-file-table-max-height, 80vh)',
            // margin: '0 1rem',                            // pre-panel value; same default below
            margin: 'var(--ob-file-table-margin, 0 1rem)',
          }}
        >
          {!isPicker && root.kind !== 'sharedIndex' && <FileTableMenu />}
          {!isPicker && <SharedRowMenu />}
          {root.kind === 'sharedIndex' ? (
            <SharedFolderList
              shares={listedShares}
              isLoading={sharesState.isLoading}
              pinnedIds={sharedPinnedIds}
              onOpen={openShare}
              onContextMenu={onSharedFolderContextMenu}
            />
          ) : showTableSkeleton ? (
            <div style={{width: '100%'}}>
              <FileTableSkeleton rowCount={6} />
            </div>
          ) : (
            <DropZone
              onDrop={onDropEvent}
              disabled={!canWrite}
              style={{height: 'inherit', width: '100%'}}
            >
              <div className='table-responsive ob-file-table'>
                <table
                  className='table table-flush table-row-bordered align-middle gs-0'
                  ref={outerRef}
                  style={fileTableStyle}
                >
                  <thead>
                    {getHeaderGroups().map((headerGroup) => (
                      <tr
                        className='fw-bolder text-muted'
                        key={headerGroup.id}
                        style={fileTableHeaderRowStyle}
                      >
                        {headerGroup.headers.map((header) => (
                          <th key={header.id} style={fileTableHeaderCellStyle}>
                            {header.isPlaceholder ? null : (
                              <div
                                {...{
                                  className: `ob-file-table-header-label${
                                    header.column.getCanSort() ? ' cursor-pointer select-none' : ''
                                  }`,
                                  onClick: header.column.getToggleSortingHandler(),
                                }}
                              >
                                {flexRender(header.column.columnDef.header, header.getContext())}
                                <span className='ob-file-table-sort-icon'>
                                  {{
                                    asc: <i className='fas fa-sort-up'></i>,
                                    desc: <i className='fas fa-sort-down'></i>,
                                  }[header.column.getIsSorted() as string] ?? null}
                                </span>
                              </div>
                            )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody ref={outerRef} data-testid='file-table-rows'>
                    {/* First at the top of My Files; not a file, so outside the row model. */}
                    {showSharedRow && (
                      <SharedRootRow
                        columnCount={getVisibleLeafColumns().length}
                        onOpen={() => goTo(SHARED_INDEX)}
                        onSelect={() => reportSelection([])}
                        onContextMenu={onSharedRootContextMenu}
                      />
                    )}
                    {getRowModel().rows.map((row) => (
                      <tr
                        id={row.id}
                        key={row.id}
                        {...(isPicker
                          ? {}
                          : {
                              draggable: true as const,
                              onDragStart: (e: React.DragEvent) =>
                                handleDragStart(e, row.original),
                              onDragEnd: handleDragEnd,
                              onContextMenu: (e: React.MouseEvent) => {
                                if (e.shiftKey === false) {
                                  e.preventDefault()
                                  showContextMenu(e)
                                  onAuxClick(row)
                                }
                              },
                            })}
                        onClick={(e) => onRowClick(e, row)}
                        onDoubleClick={(e) => onRowDoubleClick(e, row)}
                        tabIndex={0}
                        className={
                          [
                            isRowSelected(row) ? 'tr-selected' : '',
                            sharedFolderFor(row.original) ? 'ob-file-row--shared' : '',
                          ]
                            .filter(Boolean)
                            .join(' ') || undefined
                        }
                        style={{
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          ...(cssStyledRows
                            ? {}
                            : {
                                backgroundColor: isRowSelected(row)
                                  ? 'var(--ob-row-selected-bg)'
                                  : sharedFolderFor(row.original)
                                  ? 'var(--ob-shared-folder-tint)'
                                  : 'transparent',
                                borderLeft: isRowSelected(row)
                                  ? '3px solid #888e00'
                                  : '3px solid transparent',
                              }),
                        }}
                        onMouseEnter={
                          cssStyledRows
                            ? undefined
                            : (e) => {
                                if (!isRowSelected(row)) {
                                  e.currentTarget.style.borderLeft = '3px solid #dbde8e'
                                }
                              }
                        }
                        onMouseLeave={
                          cssStyledRows
                            ? undefined
                            : (e) => {
                                if (!isRowSelected(row)) {
                                  e.currentTarget.style.borderLeft = '3px solid transparent'
                                }
                              }
                        }
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} style={fileTableCellStyle}>
                            <div
                              className={FILE_TABLE_CELL_CONTENT_CLASS}
                              style={fileTableCellContentStyle}
                            >
                              {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </DropZone>
          )}
        </div>

        {!isPicker && (root.kind === 'sharedIndex' || !showTableSkeleton) && (
          <div
            className='text-muted ob-file-table-status'
            data-testid='file-table-status'
            aria-live='polite'
            style={{
              flex: '0 0 auto',
              margin: 'var(--ob-file-table-margin, 0 1rem)',
              padding: '0.375rem 0.75rem',
              fontSize: 'var(--ob-font-size-xs)',
              borderTop: '1px solid var(--ob-file-table-border-color)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {root.kind === 'sharedIndex'
              ? sharedIndexStatus(listedShares.length)
              : folderStatus}
          </div>
        )}

        {/*
          The "expecting *.psq, *.nsq…" escape hatch, rendered here because this is the only place
          that knows both the unfiltered listing and what survived the filter.
        */}
        {isPicker && !!pickFilters?.length && (
          <div className='form-check ob-file-pick__filter'>
            <input
              className='form-check-input'
              type='checkbox'
              id='file-pick-show-all'
              checked={showAllFiles}
              onChange={(e) => setShowAllFiles(e.target.checked)}
            />
            <label className='form-check-label text-muted' htmlFor='file-pick-show-all'>
              Show all files
              {!showAllFiles && hiddenByFilter > 0 ? ` (${hiddenByFilter} hidden)` : ''} — expecting{' '}
              {pickFilters.join(', ')}
            </label>
          </div>
        )}

        {isPicker && pickRefused && (
          <div className='ob-file-pick__note' role='status' data-testid='file-pick-shared-note'>
            <i className='fas fa-circle-info me-2' aria-hidden='true' />
            {SHARED_INPUT_NOTE}
          </div>
        )}

        {/*
          Ctrl/shift-click is discoverable in the Files panel, where the rows are the whole page; in a
          dialog people reasonably expect one click to be the answer. So it is spelled out, along with
          the running count, which is the only feedback that a selection is being built up.
        */}
        {isPicker && props.pick?.multiple && (
          <div className='text-muted ob-file-pick__multi-hint'>
            Ctrl-click or shift-click to choose several files
            {selectedRows.length > 0 ? ` — ${selectedRows.length} selected` : ''}
          </div>
        )}
        </div>
      </FileTableContext.Provider>
    </>
  )
}

/**
 * The Files browser. Its keyboard shortcuts and its context menu share one clipboard, so a copy
 * made with either can be pasted with the other, and one Move/Copy dialog for transfers between My
 * Files and a shared folder.
 */
function FileTable(props: FileTableProps) {
  const queryClient = useQueryClient()
  const {currentUser} = useCognitoAuth()
  const identityId = currentUser?.identity_id
  // A Move/Copy between My Files and a shared folder ends in the background: refresh both sides.
  const onTransferFinished = useCallback(
    (outcome: ShareTransferOutcome) => refreshAfterTransfer(queryClient, identityId, outcome),
    [queryClient, identityId]
  )
  // So does a move of one of the user's own shared folders within My Files; pins follow it.
  const {followMoves} = usePinActions()
  const onShareMoveFinished = useCallback(
    (outcome: ShareMoveOutcome) => {
      refreshAfterShareMove(queryClient, identityId, outcome)
      if (outcome.status === 'succeeded') {
        followMoves(shareMoveFolderMoves(outcome.intent))
      }
    },
    [queryClient, identityId, followMoves]
  )
  return (
    <FilesClipboardProvider>
      <ShareTransferProvider onFinished={onTransferFinished}>
        <ShareMoveProvider onFinished={onShareMoveFinished}>
          <FileTableBrowser {...props} />
        </ShareMoveProvider>
      </ShareTransferProvider>
    </FilesClipboardProvider>
  )
}

export {FileTable}

/**
 * Previously `collapsibleCard(FileTable)`. The HOC was dropped when Files became a lateral panel:
 * it wrapped the table in a `col-xxl` Bootstrap grid column that fights the panel's flex column,
 * and its collapse action had always been inert here — it portals into `#minimized_cards_holder`,
 * an element this app never renders, and FileTable hardcodes `hideTitle` so the collapse button
 * never appeared. `collapsible-card.tsx` is still used by the external-viewer components.
 */
export default FileTable
